<template>
  <div class="opt-sheet-editor-container">
    <div ref="sheetEditorContainer" class="opt-sheet-editor" v-show="optimiserSheet && !loading">
      <div class="opt-sheet-editor-top">
        <div class="opt-ruler-spacer" />
        <Ruler ref="horizontalRuler" orientation="horizontal" :visible="true" :showMousePosition="false" :scale="scale" :originOffset="originOffsetX" :theme="rulerTheme" />
      </div>
      <div class="opt-sheet-editor-main">
        <Ruler ref="verticalRuler" orientation="vertical" :visible="true" :scale="scale" :originOffset="originOffsetY" :theme="rulerTheme" />
        <div class="opt-sheet-view" ref="canvasContainer">
          <canvas ref="optimiserCanvas">
          </canvas>
        </div>
      </div>

      <!-- Tools -->
      <div class="opt-sheet-editor-tools">
        <StationControlPanelButton class="btn-danger" sizeClass="lg" iconStyle="fal" icon="fa-times" v-if="isSheetInEditing" @click="cancelSheetEditing()" :disabled="updatingServer">Cancel</StationControlPanelButton>
        <StationControlPanelButton class="btn-danger" sizeClass="lg" iconStyle="far" icon="fa-backspace" v-if="isSheetLayoutEditable" @click="clearSheetAllocations()" :disabled="updatingServer">Clear</StationControlPanelButton>
        <div class="mr-3" v-if="isSheetInEditing"></div>

        <StationControlPanelButton class="btn-default" sizeClass="lg" iconStyle="far" icon="fa-power-off" v-if="isSheetLayoutEditable" @click="resetSheetAllocations()" :disabled="updatingServer">Reset</StationControlPanelButton>
        <StationControlPanelButton class="btn-default" sizeClass="lg" iconStyle="far" icon="fa-search" @click="findInSheet()" :disabled="updatingServer">Search</StationControlPanelButton>
        <StationControlPanelButton class="btn-default" sizeClass="lg" iconStyle="far" icon="fa-undo" v-if="isSheetLayoutEditable" @click="undoLastAction()" :disabled="updatingServer">Undo</StationControlPanelButton>
        <StationControlPanelButton class="btn-default" sizeClass="lg" icon="fa-info-circle" @click="displayInfoSheet" :disabled="updatingServer">Info</StationControlPanelButton>
        <StationControlPanelButton class="btn-default" sizeClass="lg" icon="fa-expand" @click="zoomToFit">Fit Screen</StationControlPanelButton>
        <div class="mr-3"></div>

        <StationControlPanelButton class="btn-success" sizeClass="lg" icon="fa-undo fa-flip-horizontal" v-if="isSheetLayoutEditable" @click="rotateSheet()" :disabled="updatingServer">Rotate</StationControlPanelButton>
        <StationControlPanelButton class="btn-success" sizeClass="lg" icon="fa-magic" v-if="isSheetLayoutEditable" @click="optimiseSheet()" :disabled="updatingServer">Optimise</StationControlPanelButton>
        <StationControlPanelButton class="btn-success" sizeClass="lg" icon="fa-layer-plus" v-if="isSheetLayoutEditable" @click="autoFillSheet()" :disabled="updatingServer">Fill</StationControlPanelButton>
        <StationControlPanelButton class="btn-success" sizeClass="lg" icon="fa-exclamation-triangle" @click="addSheetDefect()" v-if="isSheetLayoutEditable" :disabled="updatingServer">Defect</StationControlPanelButton>
        <StationControlPanelButton class="btn-success" sizeClass="lg" icon="fa-plus-square" @click="addSheetBlank()" v-if="isSheetLayoutEditable" :disabled="updatingServer">Blank</StationControlPanelButton>
        <div class="mr-3" v-if="isSheetInEditing"></div>

        <StationControlPanelButton class="btn-complete" sizeClass="lg" icon="fa-scalpel-path" @click="startCuttingSheetClicked()" v-if="isSheetInEditing" :disabled="updatingServer" :badge="sheetWarningCount">Cut</StationControlPanelButton>
        <StationControlPanelButton class="btn-warning" sizeClass="lg" icon="fa-undo" @click="revertCutting()" v-if="isSheetRevertCutAvailable" :disabled="updatingServer">Revert</StationControlPanelButton>
        <StationControlPanelButton class="btn-primary" sizeClass="lg" icon="fa-external-link fa-rotate-180" @click="sendSheetPrimary()" v-if="isSheetInCutting && stationIsBreakout" :disabled="updatingServer">Primary</StationControlPanelButton>
        <StationControlPanelButton class="btn-primary" sizeClass="lg" icon="fa-print" @click="printSheetLabels()" v-if="isSheetInCutting" :disabled="updatingServer">Print</StationControlPanelButton>
        <StationControlPanelButton class="btn-primary" sizeClass="lg" icon="fa-file-export" @click="exportSheetClicked()"  v-if="isSheetExportAvailable" :disabled="updatingServer">Export</StationControlPanelButton>
        <StationControlPanelButton class="btn-primary" sizeClass="lg" icon="fa-external-link" @click="sendSheetSecondary()" v-if="isSheetInCutting && stationHasSecondaryStation" :disabled="updatingServer">Breakout</StationControlPanelButton>

        <div class="mr-3" v-if="isSheetInCutting && isSheetAllocationsCompleted"></div>
        <StationControlPanelButton class="btn-complete" sizeClass="lg" icon="fa-check-circle" @click="finishCuttingSheetClicked()" v-if="isSheetInCutting && isSheetAllocationsCompleted" :disabled="updatingServer">Finish</StationControlPanelButton>

        <!-- Developer Tools -->
        <StationControlPanelButton class="btn-primary" sizeClass="lg" icon="fa-file-export" @click="exportSheetClicked()"  v-if="isSheetInEditing && isDeveloperUser" :disabled="updatingServer">DEV OTD</StationControlPanelButton>

        <slot name="tools" />
      </div>

      <div class="opt-sheet-overlay-updating" v-if="updatingServer">
        <div>
          <h3>UPDATING</h3>
          <Spinner />
        </div>
      </div>
    </div>

    <!-- Allocation Popover Options -->
    <Popover ref="allocationPopover" sizeClass="popover-xlarge popover-no-padding" :showHeader="true" :headerTitle="selectedAllocTitle" placement="right" :optionPositionFixed="true">
      <div class="opt-editor-popover-content" v-if="getFirstSelectedAlloc()">
        <h3>Details</h3>

        <div v-if="!isSelectedSingleItem">
          {{selectedAllocations.length}} allocation{{displayCountPlural(selectedAllocations.length)}} selected
        </div>

        <!-- Panel -->
        <div v-if="(isSelectedAllocPanel || isSelectedAllocSplitPanel) && isSelectedSingleItem">
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Order</div>
            <div class="col-3 station-popover-item-value">#{{formatHyphenEmptyValue(getFirstSelectedAlloc().order.sequenceNumber)}}</div>
            <div class="col-3 station-popover-item-title">Panel Size</div>
            <div class="col-3 station-popover-item-value">{{selectedAllocPanel.panelHeight}}mm x {{selectedAllocPanel.panelWidth}}mm</div>
          </div>
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Order Status</div>
            <div class="col-3 station-popover-item-value">
              <LabelOrderStatusBadge :status="getFirstSelectedAlloc().order.status" />
            </div>
            <div class="col-3 station-popover-item-title">Customer</div>
            <div class="col-3 station-popover-item-value">{{formatHyphenEmptyValue(getFirstSelectedAlloc().order.contact.name)}}</div>
          </div>
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Despatch Type</div>
            <div class="col-3 station-popover-item-value">{{getFirstSelectedAlloc().order.despatchTaskType.name}}</div>
            <div class="col-3 station-popover-item-title">Customer Ref</div>
            <div class="col-3 station-popover-item-value">{{formatHyphenEmptyValue(getFirstSelectedAlloc().order.customerRef)}}</div>
          </div>
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Item Mark</div>
            <div class="col-3 station-popover-item-value">{{formatHyphenEmptyValue(getFirstSelectedAlloc().orderItem.mark)}}</div>
            <div class="col-3 station-popover-item-title">Order Due</div>
            <div class="col-3 station-popover-item-value">{{formatDate(getFirstSelectedAlloc().order.dateCurrentlyDue)}}</div>
          </div>
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Item Notes</div>
            <div class="col-9 station-popover-item-value">{{formatHyphenEmptyValue(getFirstSelectedAlloc().orderItem.notes)}}</div>
          </div>
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Order Notes</div>
            <div class="col-9 station-popover-item-value">{{formatHyphenEmptyValue(getFirstSelectedAlloc().order.notes)}}</div>
          </div>

          <template v-if="isSelectedAllocPanelCancelled">
            <div class="row mt-2">
              <div class="col-12">
                <h3 class="text-danger">Cancelled</h3>
              </div>
            </div>
          </template>

          <template v-if="getFirstSelectedAlloc().order.isOnHold || getFirstSelectedAlloc().order.isAccountsHold">
            <div class="row mt-2">
              <div class="col-12">
                <h3 class="text-danger">Order On Hold</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-3 station-popover-item-title">Hold Reason</div>
              <div class="col-9 station-popover-item-value">{{formatHyphenEmptyValue(getFirstSelectedAlloc().order.holdReason)}}</div>
            </div>
          </template>

          <template v-if="getFirstSelectedAlloc().order.isUrgent">
            <div class="row mt-2">
              <div class="col-12">
                <h3 class="text-danger">Urgent Order</h3>
              </div>
            </div>
          </template>

          <template v-if="isSelectedAllocPanelNCR">
            <div class="row mt-2">
              <div class="col-12">
                <h3 class="text-danger">NCR</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-3 station-popover-item-title">NCR Notes</div>
              <div class="col-9 station-popover-item-value">{{formatHyphenEmptyValue(getFirstSelectedAlloc().orderItem.ncrRequestReason)}}</div>
            </div>
          </template>

          <template v-if="isSelectedAllocPanelRecut">
            <div class="row mt-2">
              <div class="col-12">
                <h3 class="text-danger">Recut</h3>
              </div>
            </div>
            <div class="row" v-if="getFirstSelectedAlloc().productionPanel">
              <div class="col-3 station-popover-item-title">Recut Notes</div>
              <div class="col-9 station-popover-item-value">{{formatHyphenEmptyValue(getFirstSelectedAlloc().productionPanel.recutNotes)}}</div>
            </div>
          </template>

          <template v-if="getFirstSelectedAlloc().orderItem.isTemplateHandlingRequired">
            <div class="row mt-2">
              <div class="col-12">
                <h3 class="text-warning">Template Handling</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-12 station-popover-item-value" v-if="!getFirstSelectedAlloc().isPanelTemplateHandled">Template handling is required for this panel before cutting</div>
              <div class="col-12 station-popover-item-value" v-if="getFirstSelectedAlloc().isPanelTemplateHandled">Template has been processed</div>
            </div>
          </template>

          <template v-if="getFirstSelectedAlloc().isPanelCutoutsRequireHandling">
            <div class="row mt-2">
              <div class="col-12">
                <h3 class="text-warning">Cutouts Handling</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-12 station-popover-item-value" v-if="!getFirstSelectedAlloc().isPanelCutoutsHandled">
                Cutout handling is required for this panel before cutting.<br />
                <ul>
                  <li>Cutouts have been detected as requiring processing (eg, v-cuts)</li>
                  <li>Click on &quot;Info&quot; below to flag this panel as having cutouts handled</li>
                </ul>
              </div>
              <div class="col-12 station-popover-item-value" v-if="getFirstSelectedAlloc().isPanelCutoutsHandled">Cutouts have been processed</div>
            </div>
          </template>
        </div>

        <!-- Offcut -->
        <div v-if="isSelectedAllocOffcut && isSelectedSingleItem">
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Offcut Size</div>
            <div class="col-3 station-popover-item-value">{{getFirstSelectedAlloc().getBoundsHeight()}}mm x {{getFirstSelectedAlloc().getBoundsWidth()}}mm</div>
          </div>
        </div>

        <!-- Blank Panel -->
        <div v-if="isSelectedAllocBlankPanel && isSelectedSingleItem">
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Blank Size</div>
            <div class="col-3 station-popover-item-value">{{getFirstSelectedAlloc().getBoundsHeight()}}mm x {{getFirstSelectedAlloc().getBoundsWidth()}}mm</div>
          </div>
        </div>

        <!-- Waste -->
        <div v-if="isSelectedAllocWaste && isSelectedSingleItem">
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Waste Size</div>
            <div class="col-3 station-popover-item-value">{{getFirstSelectedAlloc().height}}mm x {{getFirstSelectedAlloc().width}}mm</div>
          </div>
        </div>

        <div v-if="(isSelectedAllocPanel || isSelectedAllocSplitPanel) && isSelectedSingleItem" class="mt-4">
          <StationControlPanelButton class="btn-primary" sizeClass="wide-lg" iconStyle="fal" icon="fa-info-circle" @click="editorAllocOrderItemsClicked">Order Items</StationControlPanelButton>
          <StationControlPanelButton class="btn-primary" sizeClass="wide-lg" iconStyle="fal" icon="fa-book" @click="editorAllocDocumentsClicked">Documents</StationControlPanelButton>
          <StationControlPanelButton class="btn-primary" sizeClass="wide-lg" iconStyle="fal" icon="fa-ruler-combined" v-if="isSelectedAllocPanelEditorGenerated" @click="editorAllocEditorViewClicked">Editor View</StationControlPanelButton>
        </div>

        <div class="mt-4" v-if="(isSelectedAllocBlankPanel || isSelectedAllocPanel || isSelectedAllocPanelScratchPad || isSelectedAllocOffcut || isSelectedAllocDefect || isSelectedAllocWaste) && isSheetInEditing">
          <FormHorizontalRule />
          <StationControlPanelButton class="btn-danger" iconStyle="fal" icon="fa-times" v-if="isSelectedAllocPanel || isSelectedAllocPanelScratchPad || isSelectedAllocOffcut || isSelectedAllocDefect || isSelectedAllocBlankPanel" @click="editorAllocRemoveClicked">Remove</StationControlPanelButton>
          <StationControlPanelButton class="btn-primary" iconStyle="fal" icon="fa-plus-square" v-if="isSelectedAllocWaste && isSelectItemsSameType" @click="editorAllocMakeOffcut">Offcut</StationControlPanelButton>
          <StationControlPanelButton class="btn-default" iconStyle="fal" icon="fa-info-circle" v-if="isSelectedAllocPanel && isSelectedSingleItem" @click="editorAllocInfoClicked">Info</StationControlPanelButton>
          <StationControlPanelButton class="btn-success" iconStyle="far" icon="fa-undo fa-flip-horizontal" v-if="(isSelectedAllocPanel || isSelectedAllocBlankPanel) && isSelectedSingleItem && !isSheetStockUsingGrain" @click="editorAllocRotateClicked">Rotate</StationControlPanelButton>
          <StationControlPanelButton class="btn-success" iconStyle="far" icon="fa-sync" v-if="isSelectedAllocPanel && isSelectedSingleItem" @click="editorAllocRotate180Clicked">180</StationControlPanelButton>
          <StationControlPanelButton class="btn-success" iconStyle="far" icon="fa-border-center-v" :highlight="getFirstSelectedAlloc().flippedHorz" v-if="isSelectedAllocPanel && isSelectedAllocPanelEditorGenerated && isSelectedSingleItem && !isSheetStockUsingFaces" @click="editorAllocFlipHorzClicked">Flip</StationControlPanelButton>
          <StationControlPanelButton class="btn-success" iconStyle="far" icon="fa-scalpel-path" v-if="isSelectedAllocOffcut && isSelectedSingleItem" @click="editorAllocSplitOffcutClicked">Split</StationControlPanelButton>
          <StationControlPanelButton class="btn-success" iconStyle="fal" icon="fa-lock" v-if="(isSelectedAllocOffcut || isSelectedAllocWaste) && !getFirstSelectedAlloc().isUserLocked && isSelectedSingleItem" @click="editorAllocLockClicked">Lock</StationControlPanelButton>
          <StationControlPanelButton class="btn-success" iconStyle="fal" icon="fa-lock-open" v-if="(isSelectedAllocOffcut || isSelectedAllocWaste) && getFirstSelectedAlloc().isUserLocked && isSelectedSingleItem" @click="editorAllocUnlockClicked">Unlock</StationControlPanelButton>
        </div>

        <div class="mt-4" v-if="isSheetInCutting">
          <FormHorizontalRule />
          <StationControlPanelButton class="btn-complete" sizeClass="lg" iconStyle="fal" icon="fa-check-circle" v-if="isSelectedAllocBlankPanel && getFirstSelectedAlloc().status == SheetAllocationStatusTypes.pending && isSelectedSingleItem" @click="editorAllocCompleteClicked">COMPLETE</StationControlPanelButton>
          <StationControlPanelButton class="btn-warning" sizeClass="lg" iconStyle="fal" icon="fa-undo" v-if="(isSelectedAllocPanel || isSelectedAllocBlankPanel || isSelectedAllocOffcut) && getFirstSelectedAlloc().status == SheetAllocationStatusTypes.completed && isSelectedSingleItem" @click="editorAllocRevertClicked">REVERT</StationControlPanelButton>
        </div>
        
      </div>
    </Popover>


    <Popover ref="edgePopover" sizeClass="popover-medium popover-no-padding" :showHeader="true" headerTitle="Edge" placement="right" :optionPositionFixed="true">
      <div class="opt-editor-popover-content">
        <div v-if="!isSelectedEdgeExtendable()">
          No options available for selected edge
        </div>

        <StationControlPanelButton class="btn-success" sizeClass="lg" iconStyle="far" icon="fa-arrows-alt" v-if="isSelectedEdgeExtendable()" @click="editorEdgeExtendClicked">Extend</StationControlPanelButton>
      </div>
    </Popover>

    <Popover ref="cuttingLinePopover" sizeClass="popover-medium popover-no-padding" :showHeader="true" headerTitle="Cutting Line" placement="right" :optionPositionFixed="true">
      <div class="opt-editor-popover-content">
        <h3>Cutting Line</h3>
        <div v-if="isSelectedCuttingLineHasEdgework()">
          <strong>Edgework</strong>
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Type</div>
            <div class="col-3 station-popover-item-value">{{selectedCuttingLineEdgeworkName()}}</div>
          </div>
          <div class="row no-gutters">
            <div class="col-3 station-popover-item-title">Allowance</div>
            <div class="col-3 station-popover-item-value">{{selectedCuttingLineEdgeworkAllowance()}}mm</div>
          </div>
        </div>

        <div class="mt-4">
          <FormHorizontalRule />
          <StationControlPanelButton class="btn-success" sizeClass="lg" iconStyle="far" icon="fa-arrows-alt-v" @click="editorAddReliefCut(AxisTypes.vertical)">Relief</StationControlPanelButton>
          <StationControlPanelButton class="btn-success" sizeClass="lg" iconStyle="far" icon="fa-arrows-alt-h" @click="editorAddReliefCut(AxisTypes.horizontal)">Relief</StationControlPanelButton>
        </div>
      </div>
    </Popover>

    <Popover ref="reliefCutPopover" sizeClass="popover-medium popover-no-padding" :showHeader="true" headerTitle="Relief Cut" placement="right" :optionPositionFixed="true">
      <div class="opt-editor-popover-content">
        <StationControlPanelButton class="btn-danger" sizeClass="lg" iconStyle="far" icon="fa-times" @click="editorRemoveReliefCut">Remove</StationControlPanelButton>
        <StationControlPanelButton class="btn-danger" sizeClass="wide-lg" iconStyle="far" icon="fa-times" @click="editorRemoveAllReliefCut">Remove All</StationControlPanelButton>
      </div>
    </Popover>

    <Popover ref="cutoutPopover" sizeClass="popover-medium popover-no-padding" :showHeader="true" headerTitle="Cutout" placement="right" :optionPositionFixed="true">
      <div class="opt-editor-popover-content">
        <StationControlPanelButton class="btn-success" sizeClass="wide-lg" iconStyle="far" icon="fa-scalpel-path" v-if="!isSelectedCutoutFlagged" @click="editorAddCutout">Add Cutout</StationControlPanelButton>
        <StationControlPanelButton class="btn-danger" sizeClass="wide-lg" iconStyle="far" icon="fa-times" v-if="isSelectedCutoutFlagged" @click="editorRemoveCutout">Remove</StationControlPanelButton>
      </div>
    </Popover>

    <Spinner v-if="loading" />
  </div>
</template>

<style scoped lang="scss">
$ruler-size: 18px;
.opt-sheet-editor-container {
  width: 100%;
  height: 100%;
  overflow: visible;
}
.opt-sheet-editor {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  align-items:stretch;
}
.opt-sheet-editor-top {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: $ruler-size;
  border-top: 1px solid #505050;
  border-left: 1px solid #505050;
  border-right: 1px solid #505050;
}
.opt-sheet-editor-main {
  display: flex;
  flex-direction: row;
  width: 100%;
  flex: 100%;
  align-items:stretch;
  border-left: 1px solid #505050;
  border-bottom: 1px solid #505050;
  border-right: 1px solid #505050;
}
.opt-sheet-editor-tools {
  flex: 64px;
  padding-top: 6px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}
.opt-ruler-spacer {
  width: $ruler-size;
  height: $ruler-size;
  background-color: #1E1E1E;
}
.opt-sheet-view {
  flex: 1;
  background-color: #333333;
}
.opt-sheet-view canvas {
  display: block;
}
.opt-editor-popover-content {
  padding: 0.5rem;
}
.opt-sheet-overlay-updating {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #000000;
  opacity: 0.75;
  color: #FFFFFF;
  text-align: center;
}
</style>

<script>
import _ from 'lodash'

import FormMixin from '@/mixins/form'
import ProductionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'

import Popover from '@/components/popover.vue'
import Ruler from '@/components/editor/ruler'

import StationControlPanelButton from '@/apps/glass-production-station/src/components/buttons/stationControlPanelButton'
import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'

import DefectEditDialog from '@/apps/glass-production-station/src/views/optimiser/defectEditDialog'
import BlankEditDialog from '@/apps/glass-production-station/src/views/optimiser/blankEditDialog'
import OptimiserSheetEditDialog from '@/apps/glass-production-station/src/views/optimiser/optimiserSheetEditDialog'
import OptimiserPanelEditDialog from '@/apps/glass-production-station/src/views/optimiser/optimiserPanelEditDialog'
import OptimiserSheetSearchDialog from '@/apps/glass-production-station/src/views/optimiser/optimiserSheetSearchDialog'
import OptimiserSheetEditor from '@/apps/glass-production-station/src/classes/optimiserSheetEditor'
import StationSelectInventoryBinDialog from '@/apps/glass-production-station/src/views/stationSelectInventoryBinDialog'
import OptimiserSheetPrintDialog from '@/apps/glass-production-station/src/views/optimiser/optimiserSheetPrintDialog'
import OptimiserOffcutSplitDialog from '@/apps/glass-production-station/src/views/optimiser/optimiserOffcutSplitDialog'

import EventBus from '@/utils/eventBus'

import { ProductionPanelStatusTypes, SheetAllocationTypes, SheetAllocationTypeLookup, SheetAllocationStatusTypes, OptimiserSheetStatusTypes, OrderStatusTypes, OrderStatusTypeLookup, OrderTypes, OptimiserSheetTypes, ProductionStationTypes } from '@/store/types'
import { AxisTypes } from '@/store/mathTypes'

export default {
  name: 'OptimiserSheetEditor',
  mixins: [FormMixin, ProductionStationMixin],
  components: {
    Popover,
    Ruler,
    StationControlPanelButton,
    LabelOrderStatusBadge
  },
  props: {
    productionStage: Object,
    productionStation: Object,
    optimiserSheetId: Number
  },
  data() {
    return {
      scale: 1,
      originOffsetX: 0,
      originOffsetY: 0,

      loading: false,
      loadingError: '',
      updatingServer: false,

      optimiserSheet: null,
      optimiserSheetEditor: null,

      selectedAllocations: [],
      ignoreClickEvent: false,
      sheetWarningCount: 0,

      configurationData: {
        edgeworkConfigList: [],
        accountSettings: {},
      },

      settings: {
        backgroundColour: '#141414',
      },
      rulerTheme: {
        rulerBackground: '#1E1E1E',
        rulerTickColour: '#505050',
        rulerLabelColour: '#A0A0A0',
        rulerMouseColour: '#40ef40'
      }
    }
  },
  computed: {
    stationHasSecondaryStation() {
      return this.productionStation.secondaryStationId != null
    },
    stationIsBreakout() {
      return this.productionStation.type == ProductionStationTypes.cuttingBreakout
    },
    isSheetInEditing() {
      if(!this.optimiserSheet)
        return false
      return this.optimiserSheet.status == OptimiserSheetStatusTypes.editing
    },
    isSheetInCutting() {
      if(!this.optimiserSheet)
        return false
      return this.optimiserSheet.status == OptimiserSheetStatusTypes.cutting
    },
    isSheetLayoutEditable() {
      if(!this.optimiserSheet)
        return false
      if(this.optimiserSheet.type == OptimiserSheetTypes.splitSheet)
        return false
      return this.optimiserSheet.status == OptimiserSheetStatusTypes.editing
    },
    isSheetRevertCutAvailable() {
      if(!this.optimiserSheet)
        return false

      // Can only revert in cutting
      if(!this.isSheetInCutting)
        return false

      // Cannot revert on secondary / breakout
      if(this.stationIsBreakout)
        return false

      // If any sheet allocations have been completed (scanned out) we cannot revert
      if(this.optimiserSheet.optimiserSheetAllocations.find(alloc => alloc.status == SheetAllocationStatusTypes.completed))
        return false

      return true
    },
    isSheetExportAvailable() {
      if(!this.optimiserSheet)
        return false

      // OLD: Export not available for hand cut sheets
      // JB: 5/7/2024 - No longer the case. Allow exporting for hand cut stock. Sometimes guys will still cut

      // Allow export during / cutting
      return this.isSheetInCutting
    },
    isSheetStockUsingGrain() {
      if(!this.optimiserSheet)
        return false

      if(this.optimiserSheet.stock && this.optimiserSheet.stock.isStockUsingGrain)
        return true
      return false
    },
    isSheetStockUsingFaces() {
      if(!this.optimiserSheet)
        return false

      if(this.optimiserSheet.stock && this.optimiserSheet.stock.isStockUsingFaces)
        return true
      return false
    },
    isSheetGlassOffcut() {
      if(!this.optimiserSheet)
        return false

      if(this.optimiserSheet.glassLevel && this.optimiserSheet.glassLevel.isOffcut)
        return true
      return false
    },
    isSheetAllocationsCompleted() {
      if(!this.optimiserSheet)
        return false

      var completableAllocList = this.optimiserSheet.optimiserSheetAllocations.filter(alloc => {
        if(alloc.status == SheetAllocationStatusTypes.cancelled)
          return false

        return (alloc.type == SheetAllocationTypes.blankPanel ||
                alloc.type == SheetAllocationTypes.panel ||
                alloc.type == SheetAllocationTypes.panelScratchPad ||
                alloc.type == SheetAllocationTypes.splitPanel ||
                alloc.type == SheetAllocationTypes.offcut)
      })

      // Check if all allocations are completed
      return completableAllocList.every(alloc => alloc.status == SheetAllocationStatusTypes.completed)
    },
    selectedAllocTitle() {
      if(!this.getFirstSelectedAlloc())
        return 'Allocation'

      if(this.selectedAllocations.length > 1)
        return 'Multiple Allocations'

      let alloc = this.getFirstSelectedAlloc()

      if(alloc.type == SheetAllocationTypes.panel)
        return `Panel ${alloc.productionPanel.barcode}`
      if(alloc.type == SheetAllocationTypes.defect)
        return 'Defect'
      if(alloc.type == SheetAllocationTypes.offcut)
        return 'Offcut'
      if(alloc.type == SheetAllocationTypes.waste)
        return 'Waste'
      if(alloc.type == SheetAllocationTypes.blankPanel)
        return 'Blank Panel'

      return 'Allocation'
    },
    isSelectedSingleItem() {
      return this.selectedAllocations.length == 1
    },
    hasSelectedItems() {
      return this.selectedAllocations.length > 0
    },
    isSelectItemsSameType() {
      // Check if all selected items are of the same type
      return _.uniq(this.selectedAllocations.map(item => item.type)).length <= 1
    },
    isSelectedAllocPanel() {
      if(!this.hasSelectedItems)
        return false
      return this.hasSelectedItemType(SheetAllocationTypes.panel)
    },
    isSelectedAllocSplitPanel() {
      if(!this.hasSelectedItems)
        return false
      return this.hasSelectedItemType(SheetAllocationTypes.splitPanel)
    },
    isSelectedAllocPanelScratchPad() {
      if(!this.hasSelectedItems)
        return false
      return this.hasSelectedItemType(SheetAllocationTypes.panelScratchPad)
    },
    isSelectedAllocOffcut() {
      if(!this.hasSelectedItems)
        return false
      return this.hasSelectedItemType(SheetAllocationTypes.offcut)
    },
    isSelectedAllocDefect() {
      if(!this.hasSelectedItems)
        return false
      return this.hasSelectedItemType(SheetAllocationTypes.defect)
    },
    isSelectedAllocWaste() {
      if(!this.hasSelectedItems)
        return false
      return this.hasSelectedItemType(SheetAllocationTypes.waste)
    },
    isSelectedAllocBlankPanel() {
      if(!this.hasSelectedItems)
        return false
      return this.hasSelectedItemType(SheetAllocationTypes.blankPanel)
    },
    selectedAllocPanel() {
      if(!this.hasSelectedItems)
        return null
      return this.getFirstSelectedAlloc().productionPanel
    },
    isSelectedAllocPanelNCR() {
      if(!this.hasSelectedItems)
        return false
      return this.selectedAllocations.find(item => {
        if(item.type == SheetAllocationTypes.panel || SheetAllocationTypes.panelScratchPad) {
          if(item.order && item.order.type == OrderTypes.nonChargeableRework)
            return true
        }
        return false
      }) != null
    },
    isSelectedAllocPanelRecut() {
      if(!this.hasSelectedItems)
        return false
      return this.selectedAllocations.find(item => {
        if(item.type == SheetAllocationTypes.panel || SheetAllocationTypes.panelScratchPad) {
          if(item.productionPanel && item.productionPanel.isRecut)
            return true
        }
        return false
      }) != null
    },
    isSelectedAllocPanelCancelled() {
      if(!this.hasSelectedItems)
        return false
      return this.selectedAllocations.find(item => {
        if(item.type == SheetAllocationTypes.panel || SheetAllocationTypes.panelScratchPad) {
          if(item.order && item.order.status == OrderStatusTypes.cancelled)
            return true
          if(item.orderItem && item.orderItem.isCancelled)
            return true
          if(item.productionPanel && item.productionPanel.status == ProductionPanelStatusTypes.cancelled)
            return true
        }
        return false
      }) != null
    },
    isSelectedAllocPanelEditorGenerated() {
      if(!this.hasSelectedItems)
        return false

      return this.selectedAllocations.find(item => {
        if(item.type == SheetAllocationTypes.panel || SheetAllocationTypes.panelScratchPad) {
          if(item.orderItem && item.orderItem.isEditorGeneratedItem)
            return true
        }
        return false
      }) != null
    },
    isSelectedCutoutFlagged() {
      if(!this.optimiserSheetEditor)
        return false
      if(!this.optimiserSheetEditor.isCutoutSelected())
        return false
      
      return this.optimiserSheetEditor.isSelectedCutoutFlagged()
    }
  },
  watch: {
    optimiserSheetId: function() {
      if(!this.optimiserSheetId) {
        this.optimiserSheet = null
        return
      }

      this.requestOptimiserSheetData()
    }
  },
  created() {
    this.SheetAllocationTypes = SheetAllocationTypes
    this.SheetAllocationTypeLookup = SheetAllocationTypeLookup
    this.SheetAllocationStatusTypes = SheetAllocationStatusTypes
    this.OrderTypes = OrderTypes
    this.OrderStatusTypes = OrderStatusTypes
    this.AxisTypes = AxisTypes
  },
  mounted() {
    this.requestConfigData()

    this.optimiserSheetEditor = new OptimiserSheetEditor(this, this.$refs.optimiserCanvas, this.$refs.canvasContainer, this.productionStation.id)

    this.$refs.sheetEditorContainer.addEventListener('mousewheel', this.onMouseWheel, false)
    this.$refs.sheetEditorContainer.addEventListener('mousedown', this.onMouseDown, false)
    this.$refs.sheetEditorContainer.addEventListener('mousemove', this.onMouseMove, false)
    this.$refs.sheetEditorContainer.addEventListener('mouseup', this.onMouseUp, false)
    this.$refs.sheetEditorContainer.addEventListener('click', this.onClick, false)
    this.$refs.sheetEditorContainer.addEventListener('dblclick', this.onDoubleClick, false)
    this.$refs.sheetEditorContainer.addEventListener('contextmenu', this.onContextMenu, false)
    window.addEventListener('keydown', this.onKeyDown, false)
    window.addEventListener('resize', this.onResize, false)

    EventBus.on(EventBus.EventTypes.offcutIssueCreated, this.onOffcutIssueCreated)

    this.render()
  },
  beforeDestroy() {
    this.$refs.sheetEditorContainer.removeEventListener('mousewheel', this.onMouseWheel)
    this.$refs.sheetEditorContainer.removeEventListener('mousedown', this.onMouseDown)
    this.$refs.sheetEditorContainer.removeEventListener('mousemove', this.onMouseMove)
    this.$refs.sheetEditorContainer.removeEventListener('mouseup', this.onMouseUp)
    this.$refs.sheetEditorContainer.removeEventListener('click', this.onClick)
    this.$refs.sheetEditorContainer.removeEventListener('dblclick', this.onDoubleClick)
    this.$refs.sheetEditorContainer.removeEventListener('contextmenu', this.onContextMenu)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('resize', this.onResize)

    EventBus.off(EventBus.EventTypes.offcutIssueCreated, this.onOffcutIssueCreated)
  },
  updated() {
    this.$nextTick(() => {
      this.render()
    })
  },
  methods: {
    requestConfigData() {
      Promise.all([
        this.$apiService.glassDesignerService.getOrderEditorEdgeworkList(),
        this.$apiService.adminService.getAccountSettings()
      ])
      .then(results => {
        this.configurationData.edgeworkConfigList = results[0].data
        this.configurationData.accountSettings = results[1].data
      })
      .catch(err => {
        this.displayNetworkError(err, 'Unable to load config data')
      })
    },
    requestOptimiserSheetData(liveReload = false) {
      if(!this.optimiserSheetId)
        return

      if(!liveReload)
        this.loading = true

      Promise.all([
        this.$apiService.glassProductionStationService.getOptimiserSheet(this.optimiserSheetId),
        this.$apiService.glassProductionStationService.getStationSheetConfig(this.productionStation.id, this.optimiserSheetId)
      ])
      .then(results => {
        this.loading = false

        // Ignore if the editor is sending a layout update
        if(this.optimiserSheetEditor.isSendingLayoutUpdate())
          return
          
        this.optimiserSheet = results[0].data

        this.$emit('sheetUpdated', this.optimiserSheet)

        this.$nextTick(() => {
          this.optimiserSheetEditor.updateSheet(this.optimiserSheet, { resetViewport: !liveReload })
          this.optimiserSheetEditor.updateSheetConfig(results[1].data)

          this.sheetUpdated()
        })

      })
      .catch(err => {
        this.loadingError = 'Error loading sheet data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    reloadOptimiserSheet() {
      this.requestOptimiserSheetData(true)
    },
    getFirstSelectedAlloc() {
      if(this.selectedAllocations.length == 0)
        return null
      return this.selectedAllocations[0]
    },
    hasSelectedItemType(type) {
      return this.selectedAllocations.find(item => item.type == type) != null
    },
    render() {
      this.$refs.horizontalRuler.renderRuler()
      this.$refs.verticalRuler.renderRuler()
      this.optimiserSheetEditor.render()
    },
    onMouseWheel(ev) {
      if(ev.target == this.$refs.optimiserCanvas) {
        this.$data.optimiserSheetEditor.onMouseWheelEvent(ev)
        this.updateZoomInfo()
        ev.stopPropagation()
      }
    },

    onMouseDown(ev) {
      if(ev.target == this.$refs.optimiserCanvas) {
        this.$data.optimiserSheetEditor.onMouseDownEvent(ev)
        ev.stopPropagation()

        this.ignoreClickEvent = false
      }
    },
    onMouseMove(ev) {
      if(ev.target == this.$refs.optimiserCanvas) {
        let handled = this.$data.optimiserSheetEditor.onMouseMoveEvent(ev)
        this.updateZoomInfo()

        this.ignoreClickEvent = handled
      }
    },
    onMouseUp(ev) {
      if(ev.target == this.$refs.optimiserCanvas) {
        let handled = this.$data.optimiserSheetEditor.onMouseUpEvent(ev)
        this.updateZoomInfo()

        this.ignoreClickEvent = handled
      }
    },
    onClick(ev) {
      if(ev.target == this.$refs.optimiserCanvas && !this.ignoreClickEvent) {
        
        // Allocation click
        this.selectedAllocations = this.optimiserSheetEditor.getSelectedAllocations()
        if(this.selectedAllocations.length > 0) {
          ev.stopPropagation()

          let selectionBounds = this.optimiserSheetEditor.getSelectedAllocationBounds()
          let canvasRect = this.$refs.canvasContainer.getBoundingClientRect()

          let displayPosX = selectionBounds.x * this.optimiserSheetEditor.getScale()
          let displayPosY = selectionBounds.y * this.optimiserSheetEditor.getScale()
          let displayWidth = selectionBounds.width * this.optimiserSheetEditor.getScale()
          let displayHeight = selectionBounds.height * this.optimiserSheetEditor.getScale()
          displayPosX += canvasRect.left + this.optimiserSheetEditor.getOriginOffsetX()
          displayPosY += canvasRect.top + this.optimiserSheetEditor.getOriginOffsetY()

          this.$refs.allocationPopover.displayFromPosition(displayPosX, displayPosY, displayWidth, displayHeight)
        } else {
          this.$refs.allocationPopover.hidePopover()
        }

        // Edge Click
        if(this.optimiserSheetEditor.isEdgeSelected()) {
          ev.stopPropagation()

          let edgeBounds = this.optimiserSheetEditor.getSelectedInfo().nodeRect
          let canvasRect = this.$refs.canvasContainer.getBoundingClientRect()

          let displayPosX = edgeBounds.x * this.optimiserSheetEditor.getScale()
          let displayPosY = edgeBounds.y * this.optimiserSheetEditor.getScale()
          let displayWidth = edgeBounds.width * this.optimiserSheetEditor.getScale()
          let displayHeight = edgeBounds.height * this.optimiserSheetEditor.getScale()
          displayPosX += canvasRect.left + this.optimiserSheetEditor.getOriginOffsetX()
          displayPosY += canvasRect.top + this.optimiserSheetEditor.getOriginOffsetY()
          
          this.$refs.edgePopover.displayFromPosition(displayPosX, displayPosY, displayWidth, displayHeight)
        } else {
          this.$refs.edgePopover.hidePopover()
        }

        // Cutting Line Click
        if(this.optimiserSheetEditor.isCuttingLineSelected()) {
          ev.stopPropagation()

          let cuttingLineInfo = this.optimiserSheetEditor.getSelectedInfo()
          let edgeBounds = cuttingLineInfo.rect
          let canvasRect = this.$refs.canvasContainer.getBoundingClientRect()

          let displayPosX = edgeBounds.x * this.optimiserSheetEditor.getScale()
          let displayPosY = edgeBounds.y * this.optimiserSheetEditor.getScale()
          let displayWidth = edgeBounds.width * this.optimiserSheetEditor.getScale()
          let displayHeight = edgeBounds.height * this.optimiserSheetEditor.getScale()
          displayPosX += canvasRect.left + this.optimiserSheetEditor.getOriginOffsetX()
          displayPosY += canvasRect.top + this.optimiserSheetEditor.getOriginOffsetY()
          
          this.$refs.cuttingLinePopover.displayFromPosition(displayPosX, displayPosY, displayWidth, displayHeight)
        } else {
          this.$refs.cuttingLinePopover.hidePopover()
        }

        // Relief Cut Click
        if(this.optimiserSheetEditor.isReliefCutSelected()) {
          ev.stopPropagation()

          let edgeBounds = this.optimiserSheetEditor.getSelectedInfo().rect
          let canvasRect = this.$refs.canvasContainer.getBoundingClientRect()

          let displayPosX = edgeBounds.x * this.optimiserSheetEditor.getScale()
          let displayPosY = edgeBounds.y * this.optimiserSheetEditor.getScale()
          let displayWidth = edgeBounds.width * this.optimiserSheetEditor.getScale()
          let displayHeight = edgeBounds.height * this.optimiserSheetEditor.getScale()
          displayPosX += canvasRect.left + this.optimiserSheetEditor.getOriginOffsetX()
          displayPosY += canvasRect.top + this.optimiserSheetEditor.getOriginOffsetY()
          
          this.$refs.reliefCutPopover.displayFromPosition(displayPosX, displayPosY, displayWidth, displayHeight)
        } else {
          this.$refs.reliefCutPopover.hidePopover()
        }

        // Cutout Click
        if(this.optimiserSheetEditor.isCutoutSelected()) {
          ev.stopPropagation()

          let edgeBounds = this.optimiserSheetEditor.getSelectedInfo().rect
          let canvasRect = this.$refs.canvasContainer.getBoundingClientRect()

          let displayPosX = edgeBounds.x * this.optimiserSheetEditor.getScale()
          let displayPosY = edgeBounds.y * this.optimiserSheetEditor.getScale()
          let displayWidth = edgeBounds.width * this.optimiserSheetEditor.getScale()
          let displayHeight = edgeBounds.height * this.optimiserSheetEditor.getScale()
          displayPosX += canvasRect.left + this.optimiserSheetEditor.getOriginOffsetX()
          displayPosY += canvasRect.top + this.optimiserSheetEditor.getOriginOffsetY()

          this.$refs.cutoutPopover.displayFromPosition(displayPosX, displayPosY, displayWidth, displayHeight)
        } else {
          this.$refs.cutoutPopover.hidePopover()
        }
      }
    },
    onDoubleClick(ev) {
      if(ev.target == this.$refs.optimiserCanvas) {
        // Zoom to the allocation (if under the mouse)
        this.$data.optimiserSheetEditor.onDoubleClickEvent(ev)
        this.updateZoomInfo()


        ev.stopPropagation();
        ev.preventDefault();

        // Hide the popovers from any clicks
        this.$nextTick(() => {
          this.$refs.allocationPopover.hidePopover()
          this.$refs.edgePopover.hidePopover()
          this.$refs.cuttingLinePopover.hidePopover()
          this.$refs.reliefCutPopover.hidePopover()
          this.$refs.cutoutPopover.hidePopover()
        })

      }
    },
    onKeyDown(ev) {
      // Ignore if in a textbox
      switch (ev.target.tagName.toLowerCase()) {
        case 'input':
        case 'textarea':
        case 'select':
          return
      }
      
      this.$data.optimiserSheetEditor.onKeyDownEvent(ev)
    },
    onContextMenu(ev) {
      ev.preventDefault()
      return false
    },
    onResize(ev) {
      this.optimiserSheetEditor.onResize();
      this.updateZoomInfo()
      this.render();
    },
    panelDraggedToSheet(panelData) {
      this.optimiserSheetEditor.panelDraggedToEditor(panelData)
    },

    // Popover Methods
    editorAllocOrderItemsClicked() {
      let selectedAllocs = this.optimiserSheetEditor.getSelectedAllocations()
      this.displayPanelOrderItems(selectedAllocs[0].productionPanel)

      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocDocumentsClicked() {
      let selectedAllocs = this.optimiserSheetEditor.getSelectedAllocations()
      this.displayPanelOrderDocuments(selectedAllocs[0].productionPanel)

      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocEditorViewClicked() {
      let selectedAllocs = this.optimiserSheetEditor.getSelectedAllocations()
      this.displayPanelOrderEditorView(selectedAllocs[0].productionPanel)

      this.$refs.allocationPopover.hidePopover()
    },

    editorAllocRotateClicked() {
      let selectedAlloc = this.getFirstSelectedAlloc()
      if(selectedAlloc) {
        this.optimiserSheetEditor.rotateSheetAllocation(selectedAlloc.id)
      }

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocRotate180Clicked() {
      let selectedAlloc = this.getFirstSelectedAlloc()
      if(selectedAlloc) {
        this.optimiserSheetEditor.rotateSheetAllocation(selectedAlloc.id, 180)
      }

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocFlipHorzClicked() {
      let selectedAlloc = this.getFirstSelectedAlloc()
      if(selectedAlloc) {
        this.optimiserSheetEditor.setSheetAllocationFlipped(selectedAlloc.id, !selectedAlloc.flippedHorz, selectedAlloc.flippedVert)
      }

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocFlipVertClicked() {
      let selectedAlloc = this.getFirstSelectedAlloc()
      if(selectedAlloc) {
        this.optimiserSheetEditor.setSheetAllocationFlipped(selectedAlloc.id, selectedAlloc.flippedHorz, !selectedAlloc.flippedVert)
      }

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocLockClicked() {
      this.optimiserSheetEditor.lockSheetSelectedAllocation()

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocUnlockClicked() {
      this.optimiserSheetEditor.unlockSheetSelectedAllocation()

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocSplitOffcutClicked() {
      let selectedAlloc = this.getFirstSelectedAlloc()

      this.displayModal(OptimiserOffcutSplitDialog, {}, selectedAlloc)
      .then(result => {
        if(result) {

          this.optimiserSheetEditor.splitOffcutAllocation(selectedAlloc.id, result.directionType, result.distance)
        }
      })

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocInfoClicked() {
      let selectedAlloc = this.getFirstSelectedAlloc()
      if(selectedAlloc) {

        if(selectedAlloc.type == SheetAllocationTypes.panel) {
          this.displayModal(OptimiserPanelEditDialog, {}, selectedAlloc)
          .then(result => {
            if(result) {

              this.optimiserSheetEditor.updatePanelSheetAllocationInfo(selectedAlloc.id, result)
            }
          })
        }
      }

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocRemoveClicked() {
      this.optimiserSheetEditor.removeSheetSelectedAllocation()

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },
    editorAllocCompleteClicked() {
      let selectedAlloc = this.getFirstSelectedAlloc()
      if(selectedAlloc) {
        this.$emit('sheetAllocScanned', selectedAlloc.barcode)

        this.$refs.allocationPopover.hidePopover()
      }
    },
    editorAllocRevertClicked() {
      let selectedAlloc = this.getFirstSelectedAlloc()
      if(selectedAlloc) {
        let barcode = selectedAlloc.barcode

        switch(selectedAlloc.type) {
          case SheetAllocationTypes.panel:
          case SheetAllocationTypes.panelScratchPad:
            barcode = selectedAlloc.productionPanel.barcode
            break
          case SheetAllocationTypes.offcut:
            barcode = selectedAlloc.glassLevel.barcode
            break
        }       
        
        this.confirmDeleteMessage('Revert Scan?', '', { html: 'This will revert the scanned item and put it back into pending status' })
        .then((success) => {
          if(success) {
            this.$apiService.glassProductionStationService.revertOptimiserSheetBarcode(this.productionStation.id, this.optimiserSheetId, { barcode: barcode })
            .then(response => {
              this.reloadOptimiserSheet()

              this.displayPopupAlert('success', 'Item Reverted')
            })
            .catch(err => {
              let errorInfo = this.processFormResponse(err)

              // Show errors as a popup as they will contain more details
              this.displayPopupAlert('error', 'Revert Error!', errorInfo.errorMessage, { hasTimeout: true })

              this.$refs.panelBarcodeScan.focus()
            })

          }
        })

        this.$refs.allocationPopover.hidePopover()
      }
    },
    editorAllocMakeOffcut() {
      let selectedAlloc = this.getFirstSelectedAlloc()
      if(selectedAlloc) {
        this.optimiserSheetEditor.convertAllocationToOffcut(selectedAlloc.id)
      }

      this.optimiserSheetEditor.clearSelectedAllocation()
      this.$refs.allocationPopover.hidePopover()
    },

    // Edge Popover
    isSelectedEdgeExtendable() {
      if(!this.optimiserSheetEditor)
        return false
        
      return this.optimiserSheetEditor.getSelectedInfo().selectedEdgeExtendable
    },
    editorEdgeExtendClicked() {
      this.optimiserSheetEditor.extendSelectedEdge()
      
      this.$refs.edgePopover.hidePopover()
    },

    // Cutting Line Popover
    isSelectedCuttingLineHasEdgework() {
      if(!this.optimiserSheetEditor)
        return false

      return this.optimiserSheetEditor.getSelectedInfo().hasEdgework
    },
    selectedCuttingLineEdgeworkName() {
      if(!this.optimiserSheetEditor)
        return false

      var selectedLine = this.optimiserSheetEditor.getSelectedInfo()
      if(!selectedLine.hasEdgework)
        return false

      var edgeworkConfig = this.configurationData.edgeworkConfigList.find(edgework => edgework.id == selectedLine.orderEditorEdgeworkConfigId)
      if(edgeworkConfig)
        return edgeworkConfig.name
      return null
    },
    selectedCuttingLineEdgeworkAllowance() {
      if(!this.optimiserSheetEditor)
        return 0

      var selectedLine = this.optimiserSheetEditor.getSelectedInfo()
      if(!selectedLine.hasEdgework)
        return 0

      return selectedLine.edgeworkAllowance
    },
    editorAddReliefCut(axisType) {
      this.optimiserSheetEditor.addReliefCutToSelectedCuttingLine(axisType)

      this.$refs.cuttingLinePopover.hidePopover()
    },
    editorRemoveReliefCut() {
      this.optimiserSheetEditor.removeSelectedReliefCut()

      this.$refs.reliefCutPopover.hidePopover()
    },
    editorRemoveAllReliefCut() {
      this.optimiserSheetEditor.removeSelectedAllocAllReliefCut()

      this.$refs.reliefCutPopover.hidePopover()
    },
    editorAddCutout() {
      this.optimiserSheetEditor.addSelectedCutoutToAllocation()

      this.$refs.cutoutPopover.hidePopover()
    },
    editorRemoveCutout() {
      this.optimiserSheetEditor.removeSelectedCutoutFromAllocation()

      this.$refs.cutoutPopover.hidePopover()
    },


    // Toolbar Methods
    cancelSheetEditing() {
      this.confirmDeleteMessage('Cancel Editing?', '', { html: 'Are you sure you want to cancel editing & remove the sheet from the station?<br />Any changes to the layout will be lost!' })
      .then((success) => {
        if(success) {
          this.removeSheetFromStation()
        }
      })
    },
    clearSheetAllocations() {
      this.confirmDeleteMessage('Clear sheet?', '', { html: 'This will remove all sheet allocations and result in an empty sheet' })
      .then(success => {
        if(success)
          this.optimiserSheetEditor.removeAllPanels()
      })
    },
    resetSheetAllocations() {
      this.confirmDeleteMessage('Reset sheet?', '', { html: 'This will reset the sheet allocations to defaults and any changes to the layout will be lost!' })
      .then((success) => {
        if(success) {
          this.optimiserSheetEditor.resetSheetAllocations()
        }
      })
    },
    findInSheet() {
      this.displayModal(OptimiserSheetSearchDialog, {
        optimiserSheet: this.optimiserSheet
      })
      .then(allocationList => {
        if(allocationList) {
          this.selectedAllocations = _.cloneDeep(allocationList)
          this.optimiserSheetEditor.setSelectedAllocations(allocationList)
        }
      })
    },
    undoLastAction() {
      this.optimiserSheetEditor.undoLastAction()
    },
    rotateSheet() {
      this.optimiserSheetEditor.rotateSheet()
    },
    displayInfoSheet() {
      this.displayModal(OptimiserSheetEditDialog, {
        isCreateMode: false,
        productionStageId: this.productionStage.id,
        productionStationId: this.productionStation.id,
        editing: this.isSheetLayoutEditable,
        editingSheetObj: this.optimiserSheetEditor.editingSheet
      }, this.optimiserSheet)
      .then(sheetInfo => {
        if(sheetInfo) {
          this.optimiserSheet = sheetInfo

          this.$emit('sheetUpdated', this.optimiserSheet)

          this.optimiserSheetEditor.updateSheet(this.optimiserSheet)
          this.updateZoomInfo()
          this.render()
        }
      })
    },
    zoomToFit() {
      this.optimiserSheetEditor.zoomToFitSheet()
    },
    optimiseSheet() {
      this.optimiserSheetEditor.optimiseSheet()
    },
    autoFillSheet() {
      this.optimiserSheetEditor.autoFillSheet()
    },
    addSheetDefect() {
      this.displayModal(DefectEditDialog, {
        isCreateMode: true,
        isSheetRotated: !!this.optimiserSheet.isRotated,
        sheetWidth: this.optimiserSheet.glassLevel.width,
        sheetHeight: this.optimiserSheet.glassLevel.height,
      })
      .then(defectInfo => {
        if(defectInfo) {
          this.optimiserSheetEditor.addSheetDefect(defectInfo)
        }
      })
    },
    addSheetBlank() {
      this.displayModal(BlankEditDialog, {
        isCreateMode: true,
        sheetWidth: this.optimiserSheet.glassLevel.width,
        sheetHeight: this.optimiserSheet.glassLevel.height,
      })
      .then(blankInfo => {
        if(blankInfo) {
          this.optimiserSheetEditor.addSheetBlank(blankInfo)
        }
      })
    },
    // Revert Cutting
    revertCutting() {
      this.confirmDeleteMessage('Revert Cutting?', '', { html: 'Are you sure you want to revert cutting the sheet?<br />This will revert inventory adjustments made for the sheet and linked offcuts' })
      .then((success) => {
        if(success) {
          this.$apiService.glassProductionStationService.revertCuttingSheet(this.productionStation.id, this.optimiserSheetId)
          .then(response => {
            // Force status to update UI
            this.optimiserSheet = response.data
            this.optimiserSheet.status = OptimiserSheetStatusTypes.editing

            this.$nextTick(() => {
              this.optimiserSheetEditor.updateSheet(this.optimiserSheet)
              this.updateZoomInfo()
              this.render()
            })
            
            this.successToast('Cutting reverted')
            this.$emit('sheetUpdated', this.optimiserSheet)
          })
          .catch(err => {
            console.error(err)
            this.displayNetworkError(err, 'Error reverting sheet')
          })
        }
      })
    },

    // Cutting Mode
    startCuttingSheetClicked() {
      // Make sure no scratch pad panels exist
      if(this.optimiserSheetEditor.hasScratchPadPanelAllocations()) {
        this.errorMessage('Cannot Start Cutting', 'Sheet contains panels in the scratch pad area. Move them onto the sheet or remove them')
        return
      }

      // Show any warnings (eg, small waste allocations that are below minimum trim)
      var sheetWarnings = this.optimiserSheetEditor.calculateSheetWarningList()
      if(sheetWarnings.length > 0) {
        var htmlContent = 'The following warnings were found with the sheet:<br /><br /><ul>' + sheetWarnings.map(w => `<li>${w.message}</li>`) + '</ul><br />Are you sure you want to proceed to cutting?'

        this.confirmMessage('Cutting Warnings', '', { html: htmlContent, icon: 'error', width: '44em' })
        .then((success) => {
          if(success) {
            this.submitCutSheet()
          }
        })
        return
      }     

      this.confirmMessage('Begin Cutting?', '', { html: 'Are you sure you want to begin cutting the sheet.<br />This will lock the sheet layout and cannot be edited' })
      .then((success) => {
        if(success) {
          this.submitCutSheet()          
        }          
      })
    },
    finishCuttingSheetClicked() {
      this.confirmMessage('Finish Sheet?', '', { html: 'Are you sure you want to finish the sheet.<br />This will close the sheet and will no longer be displayed' })
      .then((success) => {
        if(success) {
          this.$apiService.glassProductionStationService.finishCuttingSheet(this.productionStation.id, this.optimiserSheetId)
          .then(response => {
            this.optimiserSheetEditor.clearSheet()
            this.$emit('sheetRemoved', this.optimiserSheet)

            this.successToast('Sheet finished')
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error finishing sheet')
          })
        }
      })
    },
    submitCutSheet(sourceInventoryBinId = null) {

      let cutData = { inventoryBinId: sourceInventoryBinId }
      this.$apiService.glassProductionStationService.startCuttingSheet(this.productionStation.id, this.optimiserSheetId, cutData)
      .then(response => {
        // Force status to update UI
        this.optimiserSheet = response.data
        this.optimiserSheet.status = OptimiserSheetStatusTypes.cutting

        this.$nextTick(() => {
          this.optimiserSheetEditor.updateSheet(this.optimiserSheet)
          this.updateZoomInfo()
          this.render()
        })
        
        this.successToast('Cutting started')
        this.$emit('sheetUpdated', this.optimiserSheet)
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)

        // Check if we need to select an inventory bin
        if(errorInfo.errorData && errorInfo.errorData.requiresInventoryBin) {
          // Display inventory bin selection
          this.displayModal(StationSelectInventoryBinDialog, { 
            message: errorInfo.errorMessage,
            accountLocationId: this.productionStation.accountLocationId,
            inventoryBinList: errorInfo.errorData.inventoryBins
          })
          .then((result) => {
            if(result) {
              this.submitCutSheet(result.inventoryBinId)
            }
          })
          return
        }

        console.error(err)
        this.displayNetworkError(err, 'Error cutting sheet')
      })
    },
    printSheetLabels() {
      this.displayModal(OptimiserSheetPrintDialog, {
        productionStationId: this.productionStation.id,
        optimiserSheetId: this.optimiserSheetId
      })
      .then(() => {
      })
    },
    exportSheetClicked() {
      this.$apiService.glassProductionStationService.generateCuttingSheetExportURL(this.productionStation.id, this.optimiserSheetId, { download: true, export: true })
      .then(response => {
        // Set to download
        window.location.href = response.data.url
        
        this.displayPopupAlert('success', 'Cutting Export Created', `Cutting export file created for sheet ${this.optimiserSheetId}`)
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Export Error')
      })
    },
    sendSheetSecondary() {
      this.$apiService.glassProductionStationService.sendSheetToSecondary(this.productionStation.id, this.optimiserSheetId)
      .then(response => {
        this.$emit('sheetRemoved', this.optimiserSheet)

        this.successToast('Sheet sent to secondary')
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Error sending sheet')
      })
    },
    sendSheetPrimary() {
      this.$apiService.glassProductionStationService.sendSheetToPrimary(this.productionStation.id, this.optimiserSheetId)
      .then(response => {
        this.$emit('sheetRemoved', this.optimiserSheet)

        this.successToast('Sheet sent to primary')
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Error sending sheet')
      })
    },
    removeSheetFromStation() {
      this.$apiService.glassProductionStationService.removeSheetFromStation(this.productionStation.id, this.optimiserSheetId)
      .then(response => {
        this.optimiserSheetEditor.clearSheet()
        this.$emit('sheetRemoved', this.optimiserSheet)
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Error removing sheet')
      })
    },

    onOffcutIssueCreated(info) {
      // If the editor is using the offcut flagged with an issue, automatically remove the sheet from the station
      if(this.optimiserSheet && this.optimiserSheet.glassLevelId == info.glassLevelId) {
        this.removeSheetFromStation()
      }
    },
    onOrderUpdated(orderIdList) {
      // Order(s) in the system were updated
      // If the current sheet contains a panel from the updated order, reload the sheet
      var sheetContainsOrder = (orderIdList || []).find(orderId => this.optimiserSheetEditor.isSheetContainOrderPanel(orderId)) != null
      
      if(sheetContainsOrder) {
        this.reloadOptimiserSheet()
      }
    },
    setUpdatingServer(updating) {
      this.updatingServer = updating
    },
    hidePopovers() {
      this.$refs.allocationPopover.hidePopover()
      this.$refs.edgePopover.hidePopover()
      this.$refs.cuttingLinePopover.hidePopover()
      this.$refs.reliefCutPopover.hidePopover()
      this.$refs.cutoutPopover.hidePopover()
    },
    updateZoomInfo() {
      this.scale = this.optimiserSheetEditor.getScale()
      this.originOffsetX = this.optimiserSheetEditor.getOriginOffsetX()
      this.originOffsetY = this.optimiserSheetEditor.getOriginOffsetY()
    },
    sheetUpdated() {
      // Update warning count
      this.sheetWarningCount = this.optimiserSheetEditor.calculateSheetWarningList().length

      this.updateZoomInfo()
      this.render()
    }
  }
}
</script>